/* Localización tolerante de una cita dentro del manuscrito.
   El modelo devuelve el `excerpt` casi literal, pero suele normalizar comillas tipográficas,
   guiones largos, puntos suspensivos o espacios. La búsqueda exacta de Word falla por eso.
   Aquí normalizamos AMBOS lados, y guardamos un mapa normalizado→original para poder recortar
   el fragmento EXACTO tal y como está en el documento (que es lo que Word sabe buscar). */

const SINGLE_Q = "‘’‚‛′´`";
const DOUBLE_Q = "“”„‟″«»";
const DASHES = "‐‑‒–—―−";
const THIN_SPACES = "    ​";

export interface Normalized {
  norm: string;
  /** map[i] = índice en la cadena original del carácter norm[i] */
  map: number[];
}

/** Normaliza para comparar, conservando la correspondencia con los índices originales. */
export function normWithMap(s: string): Normalized {
  const out: string[] = [];
  const map: number[] = [];
  const push = (chars: string, origIdx: number) => {
    for (const c of chars) {
      out.push(c);
      map.push(origIdx);
    }
  };
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (SINGLE_Q.includes(ch)) push("'", i);
    else if (DOUBLE_Q.includes(ch)) push('"', i);
    else if (DASHES.includes(ch)) push("-", i);
    else if (ch === "…") push("...", i);
    else if (/\s/.test(ch) || THIN_SPACES.includes(ch)) {
      // colapsa espacios en blanco: nunca dos seguidos, nunca uno al principio
      if (out.length && out[out.length - 1] !== " ") push(" ", i);
    } else push(ch.toLowerCase(), i);
  }
  // recorta el espacio final si lo hubiera
  while (out.length && out[out.length - 1] === " ") {
    out.pop();
    map.pop();
  }
  return { norm: out.join(""), map };
}

/**
 * ¿Este párrafo es prosa que un colaborador deba reescribir?
 *
 * La portada (título, género, autoría, códigos, recuentos) y los encabezados de capítulo
 * no lo son: el agente no sabe qué hacer con ellos y los devuelve duplicados.
 * Dos señales, y hacen falta las dos: tiene cuerpo (≥4 palabras) y CIERRA como una frase.
 */
export function isProse(text: string): boolean {
  const s = text.trim();
  if (s.split(/\s+/).length < 4) return false;
  return /[.!?…"»”'’—–]$/.test(s);
}

export interface Span {
  start: number;
  end: number;
  /** true si solo casó un prefijo: el tramo es aproximado */
  approx: boolean;
}

/** Localiza `excerpt` dentro de `haystack` tolerando comillas/guiones/espacios.
 *  Devuelve los índices EN LA CADENA ORIGINAL, o null si no aparece. */
export function findSpan(haystack: string, excerpt: string, minPrefix = 40): Span | null {
  const { norm: hn, map } = normWithMap(haystack);
  const { norm: en } = normWithMap(excerpt);
  if (!en || !hn) return null;

  let idx = hn.indexOf(en);
  let approx = false;
  let len = en.length;

  // El modelo a veces recorta o alarga la cola de la cita: probamos con un prefijo.
  if (idx < 0 && en.length > minPrefix) {
    const prefix = en.slice(0, minPrefix);
    idx = hn.indexOf(prefix);
    if (idx < 0) return null;
    approx = true;
    len = Math.min(en.length, hn.length - idx); // abarca el tramo equivalente
  }
  if (idx < 0) return null;

  const last = idx + len - 1;
  if (last >= map.length) return null;
  return { start: map[idx], end: map[last] + 1, approx };
}
